import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { TypeAnimation } from "react-type-animation";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/DSH LOGOS.png";
import Banner from "../Components/Banner";
import WhyUs from "../Components/WhyUs";
import Review from "../Components/Review";
import Swal from "sweetalert2";

const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        Swal.fire({
          title: "Logged Out",
          text: "You have been logged out successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = "/login";
        });
      }
    });
  };

  const isLoggedIn =
    localStorage.getItem("token") && localStorage.getItem("user");

  const navLinks = [
    { text: "Home", path: "/" },
    { text: "About Us", path: "/about" },
    { text: "Services", path: "/services" },
    { text: "Doctors", path: "/doctors" },
    { text: "Gallery", path: "/gallery" },
    { text: "Contact Us", path: "/contact" },

    ...(isLoggedIn
      ? [
          { text: "Profile", path: "/profile" },
          {
            text: "Logout",
            path: "#",
            onClick: handleLogout,
          },
        ]
      : [
          { text: "Register", path: "/register" },
          { text: "Login", path: "/login" },
        ]),
  ];

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100vw",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          overflow: "hidden",
        }}
      >
        {/* Mobile Top Bar */}
        {isMobile && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                bgcolor: "#007ca0",
                color: "white",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <img
                  src={logo}
                  alt="DSH Logo"
                  style={{
                    height: "40px",
                    marginRight: 10,
                    background: "white",
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  DSH
                </Typography>
              </Box>
              <IconButton color="inherit" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
            </Box>

            {/* Logo below menu */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <img
                src={logo}
                alt="DSH Full Logo"
                style={{ maxWidth: "90%", height: "auto" }}
              />
            </Box>
          </>
        )}

        {/* Desktop Logo */}
        {!isMobile && (
          <Box
            sx={{
              flex: "1",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img src={logo} alt="DSH Logo" style={{ height: "300px" }} />
          </Box>
        )}

        {/* Center: Title & Typing */}
        <Box
          sx={{
            flex: isMobile ? "none" : "2",
            py: isMobile ? 4 : 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant={isMobile ? "h4" : "h3"}
            sx={{ fontWeight: "bold", color: "#007ca0", mb: 2 }}
          >
            DOCTOR SERVICE AT HOME
          </Typography>
          <TypeAnimation
            sequence={["First Time in Gwalior", 1000, ""]}
            wrapper="span"
            speed={50}
            style={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              color: "#333",
              fontWeight: "bold",
            }}
            repeat={Infinity}
          />
          <Box
          sx={{
            display: { md: "flex", marginTop:20 },
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            className="appointment-btn fw-bold text-light"
            href="/department"
          >
            Get Appointment
          </Button>
        </Box>
        </Box>
        

        {/* Desktop Sidebar Navigation */}
        {!isMobile && (
          <Box
            sx={{
              flex: "1",
              bgcolor: "#007ca0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-end",
              p: 4,
              gap: 2,
            }}
          >
            {navLinks.map((link, idx) =>
              link.onClick ? (
                <Button
                  key={idx}
                  onClick={link.onClick} // Handle logout action for "Logout" button
                  variant="contained"
                  sx={{
                    width: "80%",
                    bgcolor: "white",
                    color: "#007ca0",
                    fontWeight: "bold",
                    "&:hover": { bgcolor: "#e0f7fa" },
                  }}
                >
                  {link.text}
                </Button>
              ) : (
                <Button
                  key={idx}
                  onClick={() => navigate(link.path)}
                  variant="contained"
                  sx={{
                    width: "80%",
                    bgcolor: "white",
                    color: "#007ca0",
                    fontWeight: "bold",
                    "&:hover": { bgcolor: "#e0f7fa" },
                  }}
                >
                  {link.text}
                </Button>
              )
            )}
          </Box>
        )}
      </Box>

      {/* Drawer for Mobile Nav */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
        >
          <List>
            {navLinks.map((link, idx) => (
              <ListItem
                button
                key={idx}
                onClick={() => handleNavigation(link.path)}
              >
                <ListItemText primary={link.text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Banner />
      <WhyUs />
      <Review />
    </>
  );
};

export default Home;
